import { authAxiosInstance } from '@innovamat/ga-features';
import { authService, storage } from '@innovamat/radiance-utils';
import React, { createContext, useContext, useEffect, useState } from 'react';

declare global {
  interface Window {
    __GA_CONFIG__: RegionalConfigVariablesType;
  }
}

interface LoadedConfig {
  VITE_APOLLO_SERVER: { [key: string]: string };
  VITE_API_ORGANIZATION: { [key: string]: string };
  VITE_API_USERS: { [key: string]: string };
}

interface GeoLocation {
  country: string;
  region: string;
}

const geolocation = async (fastApiUrl: string): Promise<GeoLocation> => {
  const url = `${fastApiUrl}/api/geolocation`;
  try {
    const response = await authAxiosInstance({
      url,
      method: 'GET',
      timeout: 5000,
    });
    return response.data as GeoLocation;
  } catch (error: any) {
    return { country: 'ES', region: 'IB' };
  }
};

export const isUSRegion = async (
  geoLocation: GeoLocation
): Promise<boolean> => {
  let region: string | null = '';
  try {
    const decodedToken = authService.decodeToken(storage.tokenInfo.get()!);
    region = decodedToken.region;
  } catch (_error) {
    const query = new URLSearchParams(window.location.search);
    const dataCenterRegion = query.get('dc');
    if (dataCenterRegion?.toLocaleLowerCase() === 'usa') {
      return true;
    }
    const queryRegion =
      query.get('pr') || query.get('r') || query.get('region');
    region = dataCenterRegion || queryRegion;
    if (!region) {
      try {
        region = geoLocation.country;
        return region?.toLocaleLowerCase() === 'us';
      } catch (error) {}
    }
  }
  return Boolean(region?.toLocaleLowerCase().startsWith('us'));
};

const loadConfig = async (environment: string): Promise<LoadedConfig> => {
  let config;
  try {
    config = await import(`../config/variables-by-region.${environment}.json`);
  } catch (error) {
    console.error('Failed to load configuration:', error);
    throw error;
  }
  return config;
};

const initialConfig = {
  apolloServer: '',
  apiOrganization: '',
  apiUsers: '',
  geoLocation: {
    country: 'Unknown',
    region: 'Unknown',
    isUS: false,
  },
};

window.__GA_CONFIG__ = initialConfig;

export type RegionalConfigVariablesType = typeof initialConfig;

const RegionalConfigVariablesContext =
  createContext<RegionalConfigVariablesType>(initialConfig);

interface RegionalConfigVariablesProviderProps {
  environment: string;
  geolocationAPI: string;
  children: React.ReactNode;
}

export const RegionalConfigVariablesProvider = ({
  environment,
  children,
}: RegionalConfigVariablesProviderProps): JSX.Element => {
  const [config, setConfig] = useState<RegionalConfigVariablesType | null>(
    null
  );

  useEffect(() => {
    const fetchConfig = async (): Promise<void> => {
      try {
        const loadedConfig = await loadConfig(environment);

        const region = 'us';

        setConfig({
          apolloServer: loadedConfig.VITE_APOLLO_SERVER[region],
          apiOrganization: loadedConfig.VITE_API_ORGANIZATION[region],
          apiUsers: loadedConfig.VITE_API_USERS[region],
          geoLocation: {
            country: 'Unknown',
            region: 'Unknown',
            isUS: true,
          },
        });
      } catch (error) {
        console.error('Error loading configuration:', error);
      }
    };
    fetchConfig();
  }, [environment]);

  useEffect(() => {
    if (config) {
      window.__GA_CONFIG__ = config;
    }
  }, [config]);

  if (!config) {
    return <></>;
  }
  return (
    <RegionalConfigVariablesContext.Provider value={config}>
      {children}
    </RegionalConfigVariablesContext.Provider>
  );
};

export const useRegionalConfigVariables = (): RegionalConfigVariablesType => {
  return useContext(RegionalConfigVariablesContext);
};
